import { useEffect, useState } from "react";

/**
 * HeroDemoLoop — silent looping demo of the nutrient-calculator flow.
 * Cycles through: (1) typing an ingredient list, (2) analyzing spinner,
 * (3) nutrient bars filling up. Positioned as an ambient background behind
 * the hero H1, low-opacity so the type stays dominant.
 */

const CYCLES = [
  {
    input: ["2 large eggs", "80g rolled oats", "1 banana", "200g Greek yogurt", "15g almonds"],
    out: [
      { label: "kcal",        val: 612, color: "#C8FF3F" },
      { label: "Protein",     val: 34,  color: "#C8FF3F", unit: "g" },
      { label: "Carbs",       val: 68,  color: "#FFD84D", unit: "g" },
      { label: "Fat",         val: 22,  color: "#FF7A45", unit: "g" },
      { label: "Iron",        val: 4.1, color: "#F87171", unit: "mg" },
      { label: "Calcium",     val: 320, color: "#60A5FA", unit: "mg" },
    ],
  },
  {
    input: ["120g chicken breast", "150g brown rice", "1 cup broccoli", "1 tbsp olive oil"],
    out: [
      { label: "kcal",        val: 480, color: "#C8FF3F" },
      { label: "Protein",     val: 42,  color: "#C8FF3F", unit: "g" },
      { label: "Carbs",       val: 55,  color: "#FFD84D", unit: "g" },
      { label: "Fat",         val: 12,  color: "#FF7A45", unit: "g" },
      { label: "Fiber",       val: 6.4, color: "#34D399", unit: "g" },
      { label: "Vitamin C",   val: 89,  color: "#FBBF24", unit: "mg" },
    ],
  },
  {
    input: ["150g salmon", "1 sweet potato", "spinach handful", "½ avocado"],
    out: [
      { label: "kcal",        val: 540, color: "#C8FF3F" },
      { label: "Protein",     val: 36,  color: "#C8FF3F", unit: "g" },
      { label: "Omega-3",     val: 3.2, color: "#38BDF8", unit: "g" },
      { label: "Fat",         val: 28,  color: "#FF7A45", unit: "g" },
      { label: "Vitamin D",   val: 14,  color: "#FBBF24", unit: "µg" },
      { label: "Potassium",   val: 1220, color: "#A78BFA", unit: "mg" },
    ],
  },
];

const PHASES = { INPUT: 0, ANALYZING: 1, OUTPUT: 2 };
const PHASE_DURATIONS = [3800, 900, 4000]; // ms

export default function HeroDemoLoop({ onIngredientClick }) {
  const [cycle, setCycle] = useState(0);
  const [phase, setPhase] = useState(PHASES.INPUT);
  const [frozen, setFrozen] = useState(false);

  useEffect(() => {
    if (frozen) return;
    const t = setTimeout(() => {
      if (phase === PHASES.OUTPUT) {
        setPhase(PHASES.INPUT);
        setCycle((c) => (c + 1) % CYCLES.length);
      } else {
        setPhase((p) => p + 1);
      }
    }, PHASE_DURATIONS[phase]);
    return () => clearTimeout(t);
  }, [phase, frozen]);

  const data = CYCLES[cycle];

  const handleClick = (line) => {
    setFrozen(true);
    if (typeof onIngredientClick === "function") {
      // Pass the full recipe text (all ingredients from the frozen cycle) so
      // the calculator can parse the whole snapshot, not just one line.
      onIngredientClick({ selectedLine: line, recipe: data.input.join("\n") });
    }
  };

  const resume = (e) => {
    e.stopPropagation();
    setFrozen(false);
  };

  return (
    <div
      className="absolute inset-0 hidden lg:flex items-center justify-end pr-6 overflow-hidden"
      aria-hidden="true"
      data-testid="hero-demo-loop"
      data-frozen={frozen ? "true" : "false"}
    >
      {/* Subtle grid backdrop */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(#C8FF3F 1px, transparent 1px), linear-gradient(90deg, #C8FF3F 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      {/* Console pane */}
      <div
        className="pointer-events-auto relative w-[520px] xl:w-[600px] mr-8 xl:mr-16 border border-[#C8FF3F]/25 bg-black/40 backdrop-blur-sm rounded-2xl p-5 xl:p-6 shadow-[0_0_60px_rgba(200,255,63,0.08)]"
        style={{ opacity: 0.95 }}
        data-testid="hero-demo-panel"
      >
        {/* Header bar */}
        <div className="flex items-center justify-between border-b border-white/10 pb-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#C8FF3F]/80" />
          </div>
          <div className="flex items-center gap-2">
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/60">
              nutrient/lab · parse
            </div>
            {frozen && (
              <button
                onClick={resume}
                data-testid="hero-demo-resume"
                className="font-mono text-[9px] uppercase tracking-[0.25em] text-black bg-[#C8FF3F] px-2 py-0.5 rounded-full hover:bg-white transition-colors">
                ▶ Resume
              </button>
            )}
          </div>
        </div>

        {/* Ingredient input list — clickable */}
        <div className="font-mono text-[13px] leading-relaxed text-white/85 min-h-[128px]">
          <div className="text-[#C8FF3F]/80 text-[10px] uppercase tracking-[0.25em] mb-2">
            {phase === PHASES.INPUT ? "input · click any line" : "recipe · click any line"}
          </div>
          {data.input.map((line, i) => (
            <button
              key={`${cycle}-${i}`}
              type="button"
              onClick={() => handleClick(line)}
              data-testid={`hero-demo-line-${i}`}
              className="hero-demo-typein block w-full text-left hover:bg-[#C8FF3F]/10 hover:text-[#C8FF3F] rounded-md px-1 py-0.5 -mx-1 transition-colors cursor-pointer"
              style={{
                animationDelay: `${i * 0.55}s`,
                animationDuration: "0.5s",
              }}
            >
              <span className="text-white/50">›</span> {line}
            </button>
          ))}
        </div>

        {/* Divider + phase indicator */}
        <div className="my-4 flex items-center gap-3 text-[#C8FF3F]">
          <div className="flex-1 h-px bg-[#C8FF3F]/20" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">
            {phase === PHASES.INPUT && "parsing…"}
            {phase === PHASES.ANALYZING && "gemini 3.1 pro"}
            {phase === PHASES.OUTPUT && "nutrients"}
          </span>
          <div className="flex-1 h-px bg-[#C8FF3F]/20" />
        </div>

        {/* Analyzing spinner */}
        {phase === PHASES.ANALYZING && (
          <div className="flex items-center justify-center h-[136px]">
            <div className="hero-demo-spinner h-10 w-10 rounded-full border-2 border-[#C8FF3F]/20 border-t-[#C8FF3F]" />
          </div>
        )}

        {/* Output bars */}
        {phase === PHASES.OUTPUT && (
          <div className="grid grid-cols-2 gap-3 min-h-[136px]" key={`out-${cycle}`}>
            {data.out.map((n, i) => (
              <div key={n.label} className="space-y-1">
                <div className="flex items-baseline justify-between">
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/60">
                    {n.label}
                  </span>
                  <span
                    className="font-display font-bold text-lg tabular-nums hero-demo-countup"
                    style={{ color: n.color, animationDelay: `${0.15 + i * 0.09}s` }}
                  >
                    {n.val}
                    {n.unit ? <span className="text-[10px] font-mono ml-0.5 opacity-70">{n.unit}</span> : null}
                  </span>
                </div>
                <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full hero-demo-bar"
                    style={{
                      backgroundColor: n.color,
                      animationDelay: `${0.15 + i * 0.09}s`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Placeholder height reserver for INPUT phase to prevent layout shifts */}
        {phase === PHASES.INPUT && <div className="min-h-[136px]" />}
      </div>
    </div>
  );
}
